import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Monitor, 
  DollarSign, 
  Wrench, 
  Eye, 
  BarChart3, 
  PieChart, 
  Activity,
  ArrowRight,
  CheckCircle,
  Users,
  CreditCard,
  Home,
  ExternalLink,
  AlertCircle
} from 'lucide-react';

const SneakPeekSection = ({ scrollToSection }) => {
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  // Timeout for iframe loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!iframeLoaded && !iframeError) {
        console.log('⚠️ Iframe timeout reached - showing fallback');
        setShowFallback(true);
      }
    }, 3000); // 3 second timeout

    return () => clearTimeout(timer);
  }, [iframeLoaded, iframeError]);

  // CSP detection using postMessage
  useEffect(() => {
    const handleMessage = (event) => {
      // Listen for messages from the iframe
      if (event.origin === 'https://app.arcade.software') {
        console.log('✅ Iframe loaded successfully - received message');
        setIframeLoaded(true);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Also try to detect CSP violations
    const originalConsoleError = console.error;
    console.error = function(...args) {
      const message = args.join(' ').toLowerCase();
      if (message.includes('refused to frame') || 
          message.includes('content security policy') || 
          message.includes('x-frame-options')) {
        console.log('❌ CSP violation detected');
        setIframeError(true);
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      window.removeEventListener('message', handleMessage);
      console.error = originalConsoleError;
    };
  }, []);
  const portalFeatures = [
    {
      image: "https://www.landlordmax.com/images/screenshots/dashboard.jpg",
      title: "Property Management Dashboard",
      description: "Real-time overview of your entire portfolio with key metrics and alerts",
      icon: BarChart3,
      highlights: ["Portfolio overview", "Key metrics", "Alert notifications"]
    },
    {
      image: "https://www.landlordmax.com/images/screenshots/tenantView.png", 
      title: "Tenant & Payment Tracking",
      description: "Complete tenant profiles, payment history, and lease management in one place",
      icon: CreditCard,
      highlights: ["Tenant profiles", "Payment history", "Lease tracking"]
    },
    {
      image: "https://www.landlordmax.com/images/screenshots/tenantSubPanels.png",
      title: "Maintenance & Communication",
      description: "Streamlined maintenance requests and tenant communication workflows",
      icon: Wrench,
      highlights: ["Request management", "Communication logs", "Work orders"]
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-gradient-to-r from-blue-100 to-amber-100 text-slate-800 mb-4">
            <Eye className="w-4 h-4 mr-2" />
            Member Portal Preview
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
            Your Property Management Portal
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            See what Standard and Premium members access: professional property management tools 
            while maintaining your ownership rights and privacy.
          </p>
        </div>

        {/* Condensed Portal Features Grid */}
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          {portalFeatures.map((feature, index) => (
            <Card key={index} className="group hover:shadow-lg transition-all duration-300 overflow-hidden">
              <div className="relative">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="w-full h-40 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="absolute top-3 right-3">
                  <Badge className="bg-amber-500/90 text-white text-xs">Standard+</Badge>
                </div>
              </div>
              
              <CardContent className="p-4">
                <CardTitle className="text-lg mb-2">{feature.title}</CardTitle>
                <p className="text-slate-600 text-sm mb-3">{feature.description}</p>
                <div className="flex flex-wrap gap-2">
                  {feature.highlights.map((highlight, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {highlight}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Interactive Demo Section with CSP Fallback - ENHANCED */}
        <div className="max-w-5xl mx-auto mb-10">
          <Card className="shadow-2xl overflow-hidden border-2 border-amber-400/30 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
            <CardHeader className="bg-gradient-to-r from-amber-500/20 via-blue-500/20 to-amber-500/20 py-6 border-b border-amber-400/30">
              <div className="flex flex-col items-center justify-center space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
                    <Monitor className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500 font-serif">
                    Live Portal Demo
                  </CardTitle>
                  {!iframeError && !showFallback && (
                    <Badge className="bg-green-500/90 text-white border-green-400 px-3 py-1 text-sm">
                      <Activity className="w-4 h-4 mr-1 animate-pulse" />
                      Interactive
                    </Badge>
                  )}
                </div>
                <p className="text-amber-100 text-center text-sm max-w-2xl">
                  Experience the full member portal in action • See property management tools, tenant tracking & maintenance workflows
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-0 bg-white">
              {/* Iframe Demo (Primary) */}
              {!iframeError && !showFallback && (
                <div className="relative" style={{ height: '550px' }}>
                  <iframe
                    src="https://app.arcade.software/share/jDfbMHBA2eR82yEIF3I2"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="fullscreen"
                    allowFullScreen
                    onLoad={(e) => {
                      console.log('📡 Iframe onLoad event fired');
                      // Check if iframe actually has content (CSP detection)
                      try {
                        const iframeDoc = e.target.contentDocument || e.target.contentWindow?.document;
                        if (iframeDoc && iframeDoc.body && iframeDoc.body.innerHTML.length > 0) {
                          console.log('✅ Iframe has content - loaded successfully');
                          setIframeLoaded(true);
                        } else {
                          console.log('⚠️ Iframe loaded but no accessible content - likely CSP blocked');
                          // Wait a bit then trigger fallback
                          setTimeout(() => {
                            if (!iframeLoaded) {
                              console.log('❌ CSP timeout - showing fallback');
                              setIframeError(true);
                            }
                          }, 2000);
                        }
                      } catch (err) {
                        console.log('❌ Cannot access iframe content - CSP blocked:', err.message);
                        setIframeError(true);
                      }
                    }}
                    onError={() => {
                      console.log('❌ Iframe failed to load - network or CSP error');
                      setIframeError(true);
                    }}
                    style={{
                      border: 'none',
                      borderRadius: '0'
                    }}
                    title="Legacy Property Council Portal Demo"
                  />
                  
                  {/* Loading indicator */}
                  {!iframeLoaded && (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-amber-50 flex items-center justify-center">
                      <div className="text-center p-6">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
                        <p className="text-slate-700 font-semibold text-lg">Loading interactive demo...</p>
                        <p className="text-slate-500 text-sm mt-2">Please wait while we prepare your preview</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Fallback Demo (CSP Blocked) */}
              {(iframeError || showFallback) && (
                <div className="relative bg-gradient-to-br from-blue-50 via-white to-amber-50 flex items-center justify-center" style={{ height: '450px' }}>
                  <div className="text-center p-8 max-w-lg">
                    <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <ExternalLink className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-3">
                      Launch Full Demo Experience
                    </h3>
                    <p className="text-slate-600 mb-6 leading-relaxed">
                      Click below to open the interactive portal demo in a new tab for the best viewing experience with full functionality.
                    </p>
                    
                    {/* Primary Launch Button */}
                    <Button 
                      onClick={() => window.open('https://app.arcade.software/share/jDfbMHBA2eR82yEIF3I2', '_blank')}
                      className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-10 py-4 text-lg shadow-xl"
                      size="lg"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Open Demo in New Tab
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default SneakPeekSection;